import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { useSnackbar } from "notistack";
import { api } from "../../../../singleton/axios";
import {
  StockAnalysisReportResponse,
  GenerateStockAnalysisRequest,
  StockAnalysisReportData,
} from "../../../../types/StockAnalysisReport";
import { useTourMode } from "../../../../contexts/TourModeContext";
import { ReportQuota } from "./useReportQuota";
import { useAnalytics } from "../../../../hooks/useAnalytics";

interface StockAnalysisCheckResponse {
  exists: boolean;
  report?: StockAnalysisReportData;
  ticker: string;
}

interface UseStockAnalysisReportOptions {
  enablePolling?: boolean;
  reportId?: string | null; // Optional specific report ID to fetch
}

/**
 * Hook to check if stock analysis report exists and get it if available
 * Enhanced with polling capability for generation in progress
 * Can fetch specific report by ID or most recent if no ID provided
 */
export function useStockAnalysisReport(
  ticker: string,
  options?: UseStockAnalysisReportOptions
) {
  const { isTourMode, getMockData } = useTourMode();
  const [isPolling, setIsPolling] = useState(false); // Only start polling when explicitly requested

  const query = useQuery<StockAnalysisCheckResponse, Error>({
    queryKey: ["stockAnalysisReport", ticker, options?.reportId ?? "latest", isTourMode],
    queryFn: async () => {
      // Return mock data in tour mode
      if (isTourMode) {
        const mockReport = getMockData<StockAnalysisReportData>("aiAnalysisReport");
        if (mockReport) {
          return {
            exists: true,
            report: mockReport,
            ticker: mockReport.ticker,
          };
        }
      }
      const params: { ticker: string; reportId?: string } = { ticker };
      if (options?.reportId) {
        params.reportId = options.reportId;
      }
      const response = await api.get<StockAnalysisCheckResponse>(
        "/stock-analysis-reports/check",
        {
          params,
        }
      );
      return response.data;
    },
    enabled: isTourMode || !!ticker,
    staleTime: isTourMode ? Infinity : 5 * 60 * 1000, // Never stale in tour mode
    gcTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
    // Enable polling when requested (disabled in tour mode)
    refetchInterval: isTourMode ? false : (isPolling ? 7500 : false), // Poll every 7.5 seconds
    refetchIntervalInBackground: false, // Don't poll when window not focused
  });

  // Stop polling when report is found
  useEffect(() => {
    if (query.data?.exists && isPolling) {
      setIsPolling(false);
    }
  }, [query.data?.exists, isPolling]);

  // Auto-timeout polling after 10 minutes (reports take longer than earnings summaries)
  useEffect(() => {
    if (isPolling) {
      const timeout = setTimeout(() => {
        setIsPolling(false);
      }, 10 * 60 * 1000); // 10 minutes

      return () => {
        clearTimeout(timeout);
      };
    }
  }, [isPolling]);

  const startPolling = () => {
    setIsPolling(true);
  };

  const stopPolling = () => {
    setIsPolling(false);
  };

  return {
    ...query,
    isPolling,
    startPolling,
    stopPolling,
  };
}

// Extended response type to include quota exceeded info
interface GenerateReportResponse extends StockAnalysisReportResponse {
  needsPolling?: boolean;
  quotaExceeded?: boolean;
  quota?: {
    used: number;
    limit: number;
    remaining: number;
    resetDate: string;
  };
  /** True when this report was beyond the free weekly quota and debited credits. */
  paidWithCredits?: boolean;
}

/**
 * Hook to generate stock analysis report with 202 handling, quota handling, and polling integration
 * Creates a unique mutation for each ticker combination
 */
export function useGenerateStockAnalysisReport(ticker: string) {
  const queryClient = useQueryClient();
  const { isTourMode } = useTourMode();
  const { trackFeature } = useAnalytics();
  const { enqueueSnackbar } = useSnackbar();
  const mutationKey = `generateStockAnalysisReport-${ticker}`;

  return useMutation<
    GenerateReportResponse,
    Error,
    {
      onStartPolling?: () => void;
    }
  >({
    mutationKey: [mutationKey], // Unique key per ticker combination
    onMutate: () => {
      // Flagship feature: user kicked off an AI analysis report. Tracked on the
      // generate action (not success) so it counts every initiation.
      trackFeature("ai_report_generated", { ticker });
    },
    mutationFn: async ({ onStartPolling }) => {
      try {
        const response = await api.post<StockAnalysisReportResponse>(
          "/stock-analysis-reports/generate",
          {
            ticker,
          }
        );

        // Success - generation completed immediately
        return response.data;
      } catch (error: any) {
        // Handle 202 responses (generation already in progress)
        if (error.response?.status === 202) {
          return {
            success: false,
            needsPolling: true,
            message: error.response.data.message,
            isGenerating: true,
            report: undefined as any, // Will be populated by polling
          };
        }

        // Handle 429 responses (quota exceeded)
        if (error.response?.status === 429) {
          // Expansion signal: a member hit their weekly report cap (the trigger for a
          // paid limit increase). The quota object is already in hand here.
          trackFeature("report_quota_exceeded", {
            ticker,
            used: error.response.data.quota?.used,
            limit: error.response.data.quota?.limit,
          });
          return {
            success: false,
            quotaExceeded: true,
            message: error.response.data.message,
            error: error.response.data.error,
            quota: error.response.data.quota,
            report: undefined as any,
          };
        }

        // Re-throw other errors (real failures, not 202/429). Track for the
        // generated -> viewed/failed funnel.
        trackFeature("ai_report_failed", {
          ticker,
          error_status: error.response?.status,
        });
        throw error;
      }
    },
    onSuccess: (data, variables) => {
      // Always invalidate generation status so parent container re-checks
      // IMPORTANT: Query key must include isTourMode to match useGenerationStatus
      queryClient.invalidateQueries({
        queryKey: ["stockAnalysisGenerationStatus", ticker, isTourMode],
      });

      // Differentiate credit-paid extras from free-quota reports — the user
      // should always know when a report came off their balance, not their
      // weekly allowance.
      if (data.success && data.paidWithCredits) {
        const cachedQuota = queryClient.getQueryData<ReportQuota>(["stockAnalysisReportQuota"]);
        const price = cachedQuota?.credits?.reportPriceCredits ?? 25;
        enqueueSnackbar(`Used ${price} credits for this extra report`, { variant: "info" });
      }

      if (data.success && data.report) {
        // Generation completed immediately - update cache
        queryClient.setQueryData(
          ["stockAnalysisReport", ticker, "latest"],
          {
            exists: true,
            report: data.report,
            ticker: ticker,
          }
        );
        // Invalidate the reports list to show new report in sidebar
        queryClient.invalidateQueries({
          queryKey: ["stockAnalysisReportsList", ticker],
        });
        // Invalidate quota to reflect updated usage
        queryClient.invalidateQueries({
          queryKey: ["stockAnalysisReportQuota"],
        });
      } else if (data.needsPolling) {
        // Start polling for completion (callback for additional actions)
        if (variables.onStartPolling) {
          variables.onStartPolling();
        }
      }
      // If quotaExceeded, the UI will handle displaying the error
    },
  });
}
